/**
 * Mode Audience — interface tactile pour usage tablette en audience.
 *
 * Route directe `/dossiers/:id/audience?pdf=...&page=N`. Pas de bouton dans
 * l'UI desktop — l'avocat bookmark cette URL sur sa tablette.
 *
 * Design :
 *  - Top bar : ← Quitter · pièce active · 🔍 search globale
 *  - Center : viewer PDF plein écran (canvas pdf.js, scale ajustable)
 *  - Bottom tabs : 📄 Pièces · 📝 Doc travail · 🔖 Marqueurs · ✨ IA
 *  - Bottom sheet (drawer du bas) qui s'ouvre quand on tap un tab
 *
 * Pas de stylet/dessin libre dans cette V1 — sera ajouté en commit B.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import DictationButton from '../components/DictationButton';
import PdfPage from '../components/PdfPage';
import StrokeCanvas from '../components/StrokeCanvas';
import StylusPalette from '../components/StylusPalette';
import { useStrokes } from '../components/useStrokes';
import type { StrokeTool } from '../lib/strokes';
import Btn from '../components/ui/Btn';
import { ApiError, api } from '../lib/api';
import { getDocument } from '../lib/pdf';
import { confirmAndDeletePdf } from '../lib/pdfActions';
import { pieceLabel as fmtPieceLabel, stripPdfExtension } from '../lib/pieceRef';
import { sortPieces } from '../lib/sortPieces';
import { toast } from '../stores/toaster';
import { useAuthStore } from '../stores/auth';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import type {
  Annotation,
  Bookmark,
  ChatBlock,
  ChatResponse,
  ChatSummary,
  DossierNote,
  PdfMetadata,
} from '../lib/types';

type DrawerKind = 'pieces' | 'travail' | 'bookmarks' | 'chat' | null;

type SearchHit = {
  pdf_id: string;
  page_number: number;
  filename: string;
  snippet: string;
};

/**
 * Type unifié pour le drawer Pièces — étend PdfMetadata avec deux champs
 * pour intégrer pièces adverses / autres documents / conclusions stockées
 * dans la même liste. `_kind === 'piece'` (default) = PdfFile classique.
 */
type AudiencePiece = PdfMetadata & {
  _kind?: 'piece' | 'piece_adverse' | 'other' | 'conclusion';
  _stream_path?: string;
};

const ZOOM_STEPS = [0.6, 0.75, 0.9, 1.0, 1.15, 1.3, 1.5, 1.75, 2.0, 2.5];

function streamPathFor(p: AudiencePiece): string {
  if (p._stream_path) return p._stream_path;
  return `/api/v1/pdfs/${p.id}/stream`;
}

function kindBadge(kind: AudiencePiece['_kind']): { label: string; color: string } | null {
  switch (kind) {
    case 'piece_adverse':
      return { label: 'Adverse', color: 'bg-cat-def-pastel text-cat-def' };
    case 'other':
      return { label: 'Autre doc', color: 'bg-paper-subtle text-text-secondary' };
    case 'conclusion':
      return { label: 'Conclusion', color: 'bg-accent-light text-accent-text' };
    default:
      return null;
  }
}

export default function AudiencePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);

  const urlPdfId = searchParams.get('pdf');
  const urlPage = parseInt(searchParams.get('page') ?? '1', 10) || 1;

  const [pdfs, setPdfs] = useState<AudiencePiece[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [notes, setNotes] = useState<DossierNote[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [drawer, setDrawer] = useState<DrawerKind>(null);

  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [scaleIdx, setScaleIdx] = useState(3); // 1.0 par défaut
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // ---- Stylet (S Pen, Apple Pencil) -------------------------------------
  const [stylusActive, setStylusActive] = useState(false);
  const [stylusTool, setStylusTool] = useState<StrokeTool | 'eraser'>('pen');
  const [stylusColor, setStylusColor] = useState('#1a1a1a');
  const [stylusWidth, setStylusWidth] = useState(2);
  const [stylusForceDraw, setStylusForceDraw] = useState(false);
  const {
    strokesByPage,
    pendingCount: strokesPendingCount,
    addStroke,
    eraseStroke,
    undoLast,
    resetPage,
  } = useStrokes(urlPdfId ?? undefined);

  const activePdf = useMemo(() => pdfs.find((p) => p.id === urlPdfId) ?? null, [pdfs, urlPdfId]);

  // ---- 1. Charge dossier metadata ---------------------------------------
  // On charge les 4 sources de pièces consultables en audience :
  //   - PdfFile (pièces du dossier répressif classique)
  //   - PieceAdverse (pièces adverses)
  //   - OtherDocument (autres documents — filtrés .pdf uniquement)
  //   - ConclusionFile (conclusions déposées — filtrées .pdf uniquement)
  // Les 3 dernières apparaissent dans le drawer avec un badge distinctif et
  // streament via leur endpoint dédié (cf. streamPathFor).
  const reload = async () => {
    if (!id) return;
    setLoading(true);
    try {
      type PieceAdverseLite = {
        id: string;
        dossier_id: string;
        titre?: string | null;
        filename_original?: string | null;
        file_size_bytes?: number | null;
        has_file?: boolean;
        uploaded_at?: string;
        ocr_status?: string;
      };
      type OtherDocLite = {
        id: string;
        dossier_id: string;
        filename_original: string;
        file_size_bytes?: number | null;
        title?: string | null;
        kind?: string;
        uploaded_at?: string;
      };
      type ConclusionFileLite = {
        id: string;
        dossier_id: string;
        title?: string | null;
        filename_original?: string | null;
        file_size_bytes?: number | null;
        content_type?: string | null;
        uploaded_at?: string;
      };

      const [ps, advs, others, concls, anns, ns, bms] = await Promise.all([
        api.get<PdfMetadata[]>(`/dossiers/${id}/pdfs`),
        api
          .get<PieceAdverseLite[]>(`/dossiers/${id}/pieces-adverses`)
          .catch(() => [] as PieceAdverseLite[]),
        api
          .get<OtherDocLite[]>(`/dossiers/${id}/other-documents`)
          .catch(() => [] as OtherDocLite[]),
        api
          .get<ConclusionFileLite[]>(`/dossiers/${id}/conclusion-files`)
          .catch(() => [] as ConclusionFileLite[]),
        api.get<Annotation[]>(`/dossiers/${id}/annotations`),
        api.get<DossierNote[]>(`/dossiers/${id}/notes`).catch(() => [] as DossierNote[]),
        api.get<Bookmark[]>(`/dossiers/${id}/bookmarks`).catch(() => [] as Bookmark[]),
      ]);

      const sortedPdfs: AudiencePiece[] = sortPieces(ps).map((p) => ({ ...p, _kind: 'piece' }));

      const mapPieceAdverse = (a: PieceAdverseLite): AudiencePiece | null => {
        if (!a.has_file) return null;
        return {
          id: a.id,
          dossier_id: a.dossier_id,
          filename_original: a.filename_original ?? a.titre ?? 'Pièce adverse',
          file_size_bytes: a.file_size_bytes ?? 0,
          file_hash_sha256: '',
          page_count: null,
          is_scanned: null,
          ocr_status: a.ocr_status ?? 'pending',
          ai_extraction_status: 'n/a',
          numero_pv: null,
          titre_pv: a.titre ?? null,
          date_pv: null,
          versement_id: null,
          inventaire_entry_id: null,
          uploaded_by: '',
          uploaded_at: a.uploaded_at ?? '',
          _kind: 'piece_adverse',
          _stream_path: `/api/v1/pieces-adverses/${a.id}/stream`,
        };
      };

      const isPdfFilename = (n: string | null | undefined) =>
        typeof n === 'string' && n.toLowerCase().endsWith('.pdf');

      const mapOther = (o: OtherDocLite): AudiencePiece | null => {
        if (!isPdfFilename(o.filename_original)) return null;
        return {
          id: o.id,
          dossier_id: o.dossier_id,
          filename_original: o.filename_original,
          file_size_bytes: o.file_size_bytes ?? 0,
          file_hash_sha256: '',
          page_count: null,
          is_scanned: null,
          ocr_status: 'n/a',
          ai_extraction_status: 'n/a',
          numero_pv: null,
          titre_pv: o.title ?? null,
          date_pv: null,
          versement_id: null,
          inventaire_entry_id: null,
          uploaded_by: '',
          uploaded_at: o.uploaded_at ?? '',
          _kind: 'other',
          _stream_path: `/api/v1/other-documents/${o.id}/stream`,
        };
      };

      const mapConcl = (c: ConclusionFileLite): AudiencePiece | null => {
        const isPdfByType = c.content_type === 'application/pdf';
        if (!isPdfByType && !isPdfFilename(c.filename_original)) return null;
        return {
          id: c.id,
          dossier_id: c.dossier_id,
          filename_original: c.filename_original ?? c.title ?? 'Conclusion',
          file_size_bytes: c.file_size_bytes ?? 0,
          file_hash_sha256: '',
          page_count: null,
          is_scanned: null,
          ocr_status: 'n/a',
          ai_extraction_status: 'n/a',
          numero_pv: null,
          titre_pv: c.title ?? null,
          date_pv: null,
          versement_id: null,
          inventaire_entry_id: null,
          uploaded_by: '',
          uploaded_at: c.uploaded_at ?? '',
          _kind: 'conclusion',
          _stream_path: `/api/v1/conclusion-files/${c.id}/stream`,
        };
      };

      const extras: AudiencePiece[] = [
        ...advs.map(mapPieceAdverse).filter((x): x is AudiencePiece => x !== null),
        ...others.map(mapOther).filter((x): x is AudiencePiece => x !== null),
        ...concls.map(mapConcl).filter((x): x is AudiencePiece => x !== null),
      ];

      setPdfs([...sortedPdfs, ...extras]);
      setAnnotations(anns);
      setNotes(ns);
      setBookmarks(bms);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Si pas de pdf dans l'URL, on tombe sur la première pièce.
  useEffect(() => {
    if (loading) return;
    if (urlPdfId) return;
    if (pdfs.length === 0) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('pdf', pdfs[0]!.id);
        return next;
      },
      { replace: true }
    );
  }, [loading, urlPdfId, pdfs, setSearchParams]);

  // ---- 2. Charge le PDF actif --------------------------------------------
  useEffect(() => {
    if (!urlPdfId || !accessToken) return;
    let cancelled = false;
    let docRef: PDFDocumentProxy | null = null;
    setDocLoading(true);
    setDocError(null);
    setDoc(null);
    setPageCount(0);
    (async () => {
      try {
        const pickedPiece = pdfs.find((p) => p.id === urlPdfId) ?? null;
        const streamPath = pickedPiece
          ? streamPathFor(pickedPiece)
          : `/api/v1/pdfs/${urlPdfId}/stream`;
        const resp = await fetch(streamPath, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const buf = new Uint8Array(await resp.arrayBuffer());
        if (cancelled) return;
        docRef = await getDocument({ data: buf }).promise;
        if (cancelled) {
          await docRef.destroy();
          return;
        }
        setDoc(docRef);
        setPageCount(docRef.numPages);
      } catch (err) {
        setDocError((err as Error)?.message ?? 'Erreur de chargement');
      } finally {
        if (!cancelled) setDocLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (docRef) void docRef.destroy();
    };
    // pdfs est inclus pour résoudre le streamPath quand la pièce active est
    // une PieceAdverse / OtherDocument / ConclusionFile chargée après le
    // 1er render. Re-trigger bénin car la pièce reste la même (cancelled
    // gère la course).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPdfId, accessToken, pdfs]);

  // ---- 3. Auto-scroll vers la page demandée par ?page=N -------------------
  useEffect(() => {
    if (!doc) return;
    const el = pageRefs.current.get(urlPage);
    if (el && scrollerRef.current) {
      scrollerRef.current.scrollTo({
        top: el.offsetTop - 8,
        behavior: 'smooth',
      });
    }
  }, [doc, urlPage]);

  const switchPdf = (pdfId: string, page = 1) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('pdf', pdfId);
        if (page > 1) next.set('page', String(page));
        else next.delete('page');
        return next;
      },
      { replace: false }
    );
    setDrawer(null);
  };

  const annCountByPdf = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of annotations) {
      if (a.pdf_file_id) m.set(a.pdf_file_id, (m.get(a.pdf_file_id) ?? 0) + 1);
    }
    return m;
  }, [annotations]);

  const bmCountByPdf = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of bookmarks) {
      if (b.pdf_file_id) m.set(b.pdf_file_id, (m.get(b.pdf_file_id) ?? 0) + 1);
    }
    return m;
  }, [bookmarks]);

  if (loading && pdfs.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center bg-paper-card">
        <p className="text-sm text-text-secondary">Chargement du dossier…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-paper-card">
        <div className="max-w-md rounded-md border border-cat-def/40 bg-cat-def-pastel p-4 text-sm text-cat-def">
          {error}
          <div className="mt-2">
            <Btn size="sm" variant="secondary" onClick={() => navigate(`/dossiers/${id}`)}>
              ← Retour au dossier
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-paper-card">
      <AudienceTopBar
        dossierId={id!}
        activePdf={activePdf}
        pdfs={pdfs}
        onPickResult={(hit) => switchPdf(hit.pdf_id, hit.page_number)}
        onQuit={() => navigate(`/dossiers/${id}`)}
      />

      {/* Viewer */}
      <div
        ref={scrollerRef}
        className="min-h-0 flex-1 overflow-y-auto bg-paper-subtle"
        style={{ touchAction: 'pan-y pinch-zoom' }}
      >
        {!urlPdfId || pdfs.length === 0 ? (
          <div className="flex h-full items-center justify-center p-8 text-center">
            <p className="text-sm text-text-secondary">
              Aucune pièce dans le dossier. Reviens en mode normal pour uploader des PDF.
            </p>
          </div>
        ) : docLoading ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-text-secondary">Chargement de la pièce…</p>
          </div>
        ) : docError ? (
          <div className="flex h-full items-center justify-center p-8">
            <div className="max-w-md rounded-md border border-cat-def/40 bg-cat-def-pastel p-4 text-sm text-cat-def">
              {docError}
            </div>
          </div>
        ) : doc ? (
          <div className="flex flex-col items-center gap-3 px-2 py-3">
            {Array.from({ length: pageCount }, (_, i) => i + 1).map((pn) => (
              <div
                key={pn}
                ref={(el) => {
                  if (el) pageRefs.current.set(pn, el);
                  else pageRefs.current.delete(pn);
                }}
                className="rounded-sm bg-paper-elevated shadow"
              >
                <div className="px-3 pb-1 pt-2 text-center font-mono text-[11px] text-text-tertiary">
                  Page {pn} / {pageCount}
                </div>
                <div className="relative">
                  <PdfPage doc={doc} pageNumber={pn} scale={ZOOM_STEPS[scaleIdx]!} />
                  <StrokeCanvas
                    pageNumber={pn}
                    strokes={strokesByPage(pn)}
                    tool={stylusTool}
                    color={stylusColor}
                    width={stylusWidth}
                    forceDraw={stylusForceDraw}
                    enabled={stylusActive}
                    onStrokeComplete={addStroke}
                    onStrokeErase={eraseStroke}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {/* Floating zoom controls — pas dans la barre du bas pour ne pas
          empieter sur les drawers. En haut à droite (HUD), tap-friendly. */}
      <div className="pointer-events-none absolute right-3 top-16 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setScaleIdx(Math.min(ZOOM_STEPS.length - 1, scaleIdx + 1))}
          className="pointer-events-auto h-11 w-11 rounded-full border border-rule bg-paper-elevated text-[20px] font-medium text-text-primary shadow hover:bg-paper-card"
          title="Zoom +"
          aria-label="Zoom avant"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => setScaleIdx(Math.max(0, scaleIdx - 1))}
          className="pointer-events-auto h-11 w-11 rounded-full border border-rule bg-paper-elevated text-[20px] font-medium text-text-primary shadow hover:bg-paper-card"
          title="Zoom −"
          aria-label="Zoom arrière"
        >
          −
        </button>
        <div className="pointer-events-auto rounded-pill border border-rule bg-paper-elevated px-2 py-0.5 text-center text-[10px] font-mono text-text-tertiary">
          {Math.round(ZOOM_STEPS[scaleIdx]! * 100)}%
        </div>
      </div>

      <StylusPalette
        active={stylusActive}
        onToggleActive={() => setStylusActive((v) => !v)}
        tool={stylusTool}
        onToolChange={setStylusTool}
        color={stylusColor}
        onColorChange={setStylusColor}
        width={stylusWidth}
        onWidthChange={setStylusWidth}
        forceDraw={stylusForceDraw}
        onForceDrawToggle={() => setStylusForceDraw((v) => !v)}
        pendingCount={strokesPendingCount}
        onResetPage={() => {
          const urlPage = Number(searchParams.get('page')) || 1;
          void resetPage(urlPage);
        }}
        onUndoLast={undoLast}
        canUndo={strokesPendingCount > 0 || strokesByPage(1).length > 0}
      />

      <AudienceBottomTabs
        active={drawer}
        onSelect={setDrawer}
        pieceCount={pdfs.length}
        annotationCount={annotations.length}
        bookmarkCount={bookmarks.length}
      />

      {drawer && (
        <AudienceDrawer onClose={() => setDrawer(null)} title={drawerTitle(drawer)}>
          {drawer === 'pieces' && (
            <PiecesPanel
              pdfs={pdfs}
              activeId={urlPdfId ?? null}
              annCountByPdf={annCountByPdf}
              bmCountByPdf={bmCountByPdf}
              onPick={(p) => switchPdf(p.id)}
              onDelete={async (p) => {
                // La suppression n'est offerte que pour les PdfFile (pieces
                // du dossier répressif classique). Pour les pièces adverses
                // / autres docs / conclusions, on renvoie vers l'écran
                // dossier où la suppression est gérée avec son contexte.
                if (p._kind && p._kind !== 'piece') {
                  toast.error(
                    'Suppression non gérée en mode audience pour cette catégorie. Reviens à l\'écran dossier.'
                  );
                  return;
                }
                if (await confirmAndDeletePdf(p.id, p.filename_original)) {
                  await reload();
                  if (p.id === urlPdfId) {
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.delete('pdf');
                      next.delete('page');
                      return next;
                    });
                  }
                }
              }}
            />
          )}
          {drawer === 'travail' && (
            <TravailPanel
              annotations={annotations}
              notes={notes}
              pdfs={pdfs}
              onPickRef={(pdfId, page) => switchPdf(pdfId, page)}
            />
          )}
          {drawer === 'bookmarks' && (
            <BookmarksPanel
              bookmarks={bookmarks}
              pdfs={pdfs}
              onPick={(pdfId, page) => switchPdf(pdfId, page)}
              onChange={() => void reload()}
            />
          )}
          {drawer === 'chat' && <ChatDrawer dossierId={id!} />}
        </AudienceDrawer>
      )}
    </div>
  );
}

function drawerTitle(d: DrawerKind): string {
  if (d === 'pieces') return 'Pièces du dossier';
  if (d === 'travail') return 'Doc travail (annotations · notes)';
  if (d === 'bookmarks') return 'Marqueurs (Dossier de travail)';
  if (d === 'chat') return 'Recherche IA';
  return '';
}

// ---------------------------------------------------------------------------
// Top bar : retour + label pièce + recherche text globale
// ---------------------------------------------------------------------------

function AudienceTopBar({
  dossierId,
  activePdf,
  pdfs,
  onPickResult,
  onQuit,
}: {
  dossierId: string;
  activePdf: PdfMetadata | null;
  pdfs: PdfMetadata[];
  onPickResult: (hit: SearchHit) => void;
  onQuit: () => void;
}) {
  const [search, setSearch] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  // Recherche full-text via /search (server-side OCR full-text si dispo).
  // Fallback : filtre client-side sur les filenames pour garantir un retour
  // immédiat même si le backend search est indispo.
  const runSearch = async (q: string) => {
    if (q.length < 2) {
      setHits([]);
      return;
    }
    setSearching(true);
    try {
      type SearchResp = {
        hits?: { pdf_file_id: string; page_number: number; snippet: string }[];
      };
      const resp = await api
        .get<SearchResp>(`/dossiers/${dossierId}/search?q=${encodeURIComponent(q)}`)
        .catch(() => ({ hits: [] }) as SearchResp);
      const ocrHits: SearchHit[] = (resp.hits ?? []).map((h) => {
        const pdf = pdfs.find((p) => p.id === h.pdf_file_id);
        return {
          pdf_id: h.pdf_file_id,
          page_number: h.page_number,
          filename: pdf ? fmtPieceLabel(pdf) : '?',
          snippet: h.snippet,
        };
      });
      // Combine avec un filtre filename simple.
      const lc = q.toLowerCase();
      const fileHits: SearchHit[] = pdfs
        .filter(
          (p) =>
            p.filename_original.toLowerCase().includes(lc) ||
            (p.numero_pv ?? '').toLowerCase().includes(lc)
        )
        .map((p) => ({
          pdf_id: p.id,
          page_number: 1,
          filename: fmtPieceLabel(p),
          snippet: p.titre_pv ? `Titre : ${p.titre_pv}` : '',
        }));
      // Dédup par (pdf_id, page).
      const seen = new Set<string>();
      const merged: SearchHit[] = [];
      for (const h of [...ocrHits, ...fileHits]) {
        const key = `${h.pdf_id}-${h.page_number}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(h);
      }
      setHits(merged.slice(0, 20));
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => void runSearch(search.trim()), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <header className="z-10 flex items-center gap-2 border-b border-rule bg-paper-elevated px-2 py-2">
      <button
        type="button"
        onClick={onQuit}
        className="flex h-11 items-center gap-1 rounded-md border border-rule bg-paper-card px-3 text-[13px] text-text-primary hover:bg-paper-subtle"
      >
        ← Quitter
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12px] font-medium text-text-primary">
          {activePdf ? fmtPieceLabel(activePdf) : 'Aucune pièce'}
        </p>
        {activePdf && (
          <p className="truncate font-mono text-[10px] text-text-tertiary">
            {activePdf.numero_pv ? `PV n° ${activePdf.numero_pv} · ` : ''}
            {activePdf.page_count != null ? `${activePdf.page_count} pages` : ''}
          </p>
        )}
      </div>
      <div className="relative flex w-72 items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="🔍 Rechercher · 🎤"
          className="h-11 flex-1 rounded-md border border-rule bg-paper-card px-3 text-[13px] placeholder:text-text-tertiary focus:border-accent focus:outline-none"
        />
        <DictationButton
          size="md"
          onTranscript={(text) => setSearch((prev) => (prev ? `${prev.trimEnd()} ${text}` : text))}
        />
        {open && search.trim().length >= 2 && (
          <div className="absolute right-0 top-full z-20 mt-1 max-h-80 w-80 overflow-y-auto rounded-md border border-rule bg-paper-elevated shadow-md">
            {searching ? (
              <p className="p-3 text-center text-[12px] text-text-tertiary">Recherche…</p>
            ) : hits.length === 0 ? (
              <p className="p-3 text-center text-[12px] text-text-tertiary">Aucun résultat</p>
            ) : (
              <ul>
                {hits.map((h, i) => (
                  <li key={`${h.pdf_id}-${h.page_number}-${i}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onPickResult(h);
                        setOpen(false);
                        setSearch('');
                      }}
                      className="block w-full px-3 py-2 text-left text-[12px] hover:bg-paper-card"
                    >
                      <div className="truncate font-medium text-text-primary">{h.filename}</div>
                      <div className="truncate text-[10px] text-text-tertiary">
                        Page {h.page_number}
                        {h.snippet && ` · ${h.snippet.slice(0, 60)}`}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Bottom tabs
// ---------------------------------------------------------------------------

function AudienceBottomTabs({
  active,
  onSelect,
  pieceCount,
  annotationCount,
  bookmarkCount,
}: {
  active: DrawerKind;
  onSelect: (k: DrawerKind) => void;
  pieceCount: number;
  annotationCount: number;
  bookmarkCount: number;
}) {
  const tab = (k: Exclude<DrawerKind, null>, icon: string, label: string, badge?: number) => {
    const isActive = active === k;
    return (
      <button
        type="button"
        onClick={() => onSelect(isActive ? null : k)}
        className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] uppercase tracking-[0.5px] transition ${
          isActive
            ? 'bg-accent-light text-accent-text'
            : 'text-text-secondary hover:bg-paper-subtle'
        }`}
      >
        <span className="relative text-[20px] leading-none" aria-hidden>
          {icon}
          {badge !== undefined && badge > 0 && (
            <span className="absolute -right-2 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent-text px-1 text-[9px] font-medium text-paper-elevated">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        <span>{label}</span>
      </button>
    );
  };

  return (
    <nav className="z-10 flex items-stretch border-t border-rule bg-paper-elevated">
      {tab('pieces', '📄', 'Pièces', pieceCount)}
      {tab('travail', '📝', 'Doc trav.', annotationCount)}
      {tab('bookmarks', '🔖', 'Marqueurs', bookmarkCount)}
      {tab('chat', '✨', 'IA')}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Bottom drawer (sheet)
// ---------------------------------------------------------------------------

function AudienceDrawer({
  onClose,
  title,
  children,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Fermer"
        onClick={onClose}
        className="absolute inset-0 z-20 bg-black/30"
      />
      {/* Sheet */}
      <div className="absolute inset-x-0 bottom-[64px] z-30 max-h-[70vh] overflow-hidden rounded-t-lg bg-paper-elevated shadow-lg">
        <header className="flex items-center justify-between border-b border-rule px-4 py-2.5">
          <h3 className="font-serif text-[14px] font-medium text-text-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded text-text-tertiary hover:bg-paper-subtle hover:text-text-primary"
            aria-label="Fermer le panneau"
          >
            ✕
          </button>
        </header>
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 48px)' }}>
          {children}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Drawer 1 : Liste des pièces
// ---------------------------------------------------------------------------

function PiecesPanel({
  pdfs,
  activeId,
  annCountByPdf,
  bmCountByPdf,
  onPick,
  onDelete,
}: {
  pdfs: AudiencePiece[];
  activeId: string | null;
  annCountByPdf: Map<string, number>;
  bmCountByPdf: Map<string, number>;
  onPick: (p: AudiencePiece) => void;
  onDelete: (p: AudiencePiece) => void;
}) {
  if (pdfs.length === 0) {
    return <p className="p-6 text-center text-sm text-text-tertiary">Aucune pièce.</p>;
  }
  return (
    <ul className="divide-y divide-rule-soft">
      {pdfs.map((p) => {
        const isActive = p.id === activeId;
        const ann = annCountByPdf.get(p.id) ?? 0;
        const bm = bmCountByPdf.get(p.id) ?? 0;
        const badge = kindBadge(p._kind);
        const isPdfFile = !p._kind || p._kind === 'piece';
        return (
          <li key={p.id} className={isActive ? 'bg-accent-lighter' : ''}>
            <div className="flex items-center gap-3 px-4 py-3">
              <button type="button" onClick={() => onPick(p)} className="flex-1 text-left">
                <div className="flex items-baseline gap-2">
                  <span className="block flex-1 truncate text-[13px] font-medium text-text-primary">
                    {isPdfFile ? fmtPieceLabel(p) : (p.titre_pv || p.filename_original)}
                  </span>
                  {badge && (
                    <span
                      className={`flex-shrink-0 rounded-pill px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.5px] ${badge.color}`}
                    >
                      {badge.label}
                    </span>
                  )}
                  {p.numero_pv && (
                    <span className="font-mono text-[10px] text-text-tertiary">
                      PV n° {p.numero_pv}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-text-tertiary">
                  {p.page_count != null && <span>{p.page_count} pages</span>}
                  {ann > 0 && (
                    <span className="text-accent-text">
                      · {ann} annotation{ann > 1 ? 's' : ''}
                    </span>
                  )}
                  {bm > 0 && <span>· 🔖 {bm}</span>}
                </div>
              </button>
              {isPdfFile && (
              <button
                type="button"
                onClick={() => onDelete(p)}
                className="flex h-9 w-9 items-center justify-center rounded text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def"
                title="Supprimer la pièce"
                aria-label="Supprimer la pièce"
              >
                🗑
              </button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Drawer 2 : Doc travail (annotations + notes)
// ---------------------------------------------------------------------------

function TravailPanel({
  annotations,
  notes,
  pdfs,
  onPickRef,
}: {
  annotations: Annotation[];
  notes: DossierNote[];
  pdfs: PdfMetadata[];
  onPickRef: (pdfId: string, page: number) => void;
}) {
  const pdfById = useMemo(() => {
    const m = new Map<string, PdfMetadata>();
    for (const p of pdfs) m.set(p.id, p);
    return m;
  }, [pdfs]);

  if (annotations.length === 0 && notes.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-text-tertiary">
        Aucune annotation ni note pour ce dossier.
      </p>
    );
  }

  return (
    <div className="space-y-3 p-3">
      {annotations.length > 0 && (
        <section>
          <h4 className="mb-1.5 px-1 font-mono text-[10px] uppercase tracking-[0.5px] text-text-tertiary">
            Annotations · {annotations.length}
          </h4>
          <ul className="space-y-1.5">
            {annotations.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (a.pdf_file_id) onPickRef(a.pdf_file_id, a.page_number);
                  }}
                  className="block w-full rounded-md border border-rule bg-paper-card px-3 py-2 text-left hover:bg-paper-subtle"
                >
                  <div className="text-[10px] font-mono uppercase tracking-[0.5px] text-text-tertiary">
                    {a.category} ·{' '}
                    {a.ref_titre_pv ??
                      (a.pdf_file_id ? fmtPieceLabel(pdfById.get(a.pdf_file_id)) : '?')}
                    {' · p.'}
                    {a.page_number}
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-[12px] text-text-primary">
                    « {a.text_extract} »
                  </p>
                  {a.comment && (
                    <p className="mt-0.5 line-clamp-2 text-[11px] italic text-text-secondary">
                      {a.comment}
                    </p>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
      {notes.length > 0 && (
        <section>
          <h4 className="mb-1.5 px-1 font-mono text-[10px] uppercase tracking-[0.5px] text-text-tertiary">
            Notes libres · {notes.length}
          </h4>
          <ul className="space-y-1.5">
            {notes.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (n.pdf_file_id) onPickRef(n.pdf_file_id, n.page_number ?? 1);
                  }}
                  className="block w-full rounded-md border border-rule bg-paper-card px-3 py-2 text-left hover:bg-paper-subtle"
                >
                  <div className="text-[10px] font-mono uppercase tracking-[0.5px] text-text-tertiary">
                    {n.pdf_file_id
                      ? `${fmtPieceLabel(pdfById.get(n.pdf_file_id))}${n.page_number ? ` · p.${n.page_number}` : ''}`
                      : 'Note dossier'}
                  </div>
                  <p className="mt-0.5 line-clamp-3 whitespace-pre-wrap text-[12px] text-text-primary">
                    {n.content}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drawer 3 : Marqueurs (bookmarks = Dossier de travail)
// ---------------------------------------------------------------------------

function BookmarksPanel({
  bookmarks,
  pdfs,
  onPick,
  onChange,
}: {
  bookmarks: Bookmark[];
  pdfs: PdfMetadata[];
  onPick: (pdfId: string, page: number) => void;
  onChange: () => void;
}) {
  const pdfById = useMemo(() => {
    const m = new Map<string, PdfMetadata>();
    for (const p of pdfs) m.set(p.id, p);
    return m;
  }, [pdfs]);

  const removeBookmark = async (b: Bookmark) => {
    if (!window.confirm('Retirer ce marqueur ?')) return;
    try {
      await api.delete(`/bookmarks/${b.id}`);
      onChange();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  if (bookmarks.length === 0) {
    return (
      <p className="p-6 text-center text-sm text-text-tertiary">
        Aucun marqueur. Reviens en mode normal pour en poser depuis le viewer.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-rule-soft">
      {bookmarks.map((b) => {
        const pdf = b.pdf_file_id ? pdfById.get(b.pdf_file_id) : null;
        if (!pdf) return null;
        return (
          <li key={b.id} className="flex items-center gap-3 px-4 py-3">
            <button
              type="button"
              onClick={() => onPick(pdf.id, b.page_number ?? 1)}
              className="flex-1 text-left"
            >
              <div className="text-[13px] font-medium text-text-primary">
                {b.page_number === null ? '🏷★ Pièce entière' : `🏷 Page ${b.page_number}`}
              </div>
              <div className="mt-0.5 truncate text-[11px] text-text-tertiary">
                {pdf.titre_pv || pdf.filename_original}
              </div>
            </button>
            <button
              type="button"
              onClick={() => void removeBookmark(b)}
              className="flex h-9 w-9 items-center justify-center rounded text-text-tertiary hover:bg-cat-def-pastel hover:text-cat-def"
              aria-label="Retirer le marqueur"
            >
              🗑
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Drawer 4 : Chat IA — version compacte
// ---------------------------------------------------------------------------

function ChatDrawer({ dossierId }: { dossierId: string }) {
  const [active, setActive] = useState<ChatResponse | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadList = async () => {
    try {
      const cs = await api.get<ChatSummary[]>(`/dossiers/${dossierId}/chats`);
      if (cs.length > 0) {
        const c = await api.get<ChatResponse>(`/dossiers/${dossierId}/chats/${cs[0]!.id}`);
        setActive(c);
      } else {
        // Fallback : legacy /chat (qui crée une session si vide).
        const c = await api.get<ChatResponse>(`/dossiers/${dossierId}/chat`);
        setActive(c);
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossierId]);

  const send = async () => {
    if (!draft.trim() || sending || !active) return;
    setSending(true);
    const q = draft.trim();
    setDraft('');
    try {
      const c = await api.post<ChatResponse>(
        `/dossiers/${dossierId}/chats/${active.chat_id}/messages`,
        { content: q }
      );
      setActive(c);
    } catch (err) {
      setDraft(q);
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <p className="p-6 text-center text-sm text-text-tertiary">Chargement…</p>;
  }
  const messages = active?.messages ?? [];

  return (
    <div className="flex h-[60vh] flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
        {messages.length === 0 ? (
          <p className="rounded-md border border-dashed border-rule-strong bg-paper-card p-6 text-center text-[12px] text-text-secondary">
            Pose une question sur le dossier — Claude répondra avec citations cliquables.
          </p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={
                m.role === 'user'
                  ? 'ml-auto max-w-[85%] rounded-md border border-rule bg-paper-card px-3 py-2'
                  : 'mr-auto max-w-[95%] rounded-md border border-rule bg-paper-elevated px-3 py-2'
              }
            >
              <p className="mb-0.5 font-mono text-[9px] uppercase tracking-[0.5px] text-text-tertiary">
                {m.role === 'user' ? 'Question' : 'Claude'}
              </p>
              {m.role === 'user' ? (
                <p className="whitespace-pre-wrap text-[12px] text-text-primary">{m.content}</p>
              ) : (
                <ChatAssistantBlocks blocks={m.citations} dossierId={dossierId} />
              )}
            </div>
          ))
        )}
        {sending && (
          <div className="mr-auto max-w-[95%] rounded-md border border-rule bg-paper-elevated px-3 py-2">
            <p className="text-[12px] italic text-text-tertiary">Réflexion…</p>
          </div>
        )}
      </div>
      <div className="border-t border-rule p-2">
        <div className="flex items-end gap-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Pose une question · 🎤 dictée"
            rows={2}
            disabled={sending}
            className="h-[64px] flex-1 resize-none rounded-md border border-rule-strong bg-paper-elevated p-2 text-[12px] focus:border-accent focus:outline-none"
          />
          <DictationButton
            size="md"
            disabled={sending}
            onTranscript={(text) => setDraft((prev) => (prev ? `${prev.trimEnd()} ${text}` : text))}
          />
          <Btn
            variant="primary"
            onClick={() => void send()}
            disabled={sending || !draft.trim()}
            className="h-[64px] min-w-[64px]"
          >
            {sending ? '…' : 'Envoyer'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

function ChatAssistantBlocks({
  blocks,
  dossierId,
}: {
  blocks: ChatBlock[] | null;
  dossierId: string;
}) {
  if (!blocks || blocks.length === 0) return null;
  return (
    <div className="space-y-1">
      {blocks.map((b, i) => (
        <div key={i}>
          <p className="whitespace-pre-wrap text-[12px] leading-[1.5] text-text-primary">
            {b.text}
          </p>
          {b.citations && b.citations.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {b.citations.map((c, j) => {
                const ctx = c.context;
                const cleanLabel =
                  (ctx?.filename && stripPdfExtension(ctx.filename)) ||
                  (c.document_title && stripPdfExtension(c.document_title)) ||
                  'pièce';
                if (!ctx?.pdf_id)
                  return (
                    <span
                      key={j}
                      className="rounded-pill bg-paper-subtle px-1.5 py-0.5 text-[10px] text-text-tertiary"
                    >
                      🔗 {cleanLabel}
                    </span>
                  );
                const url = ctx.page
                  ? `/dossiers/${dossierId}/audience?pdf=${ctx.pdf_id}&page=${ctx.page}`
                  : `/dossiers/${dossierId}/audience?pdf=${ctx.pdf_id}`;
                return (
                  <Link
                    key={j}
                    to={url}
                    className="inline-flex items-center gap-1 rounded-pill border border-accent/30 bg-accent-light px-1.5 py-0.5 text-[10px] font-medium text-accent-text no-underline"
                    title={c.cited_text ?? undefined}
                  >
                    🔗 {cleanLabel}
                    {ctx.page && <span className="font-mono">p.{ctx.page}</span>}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
